import React, { createContext, useContext, useState, useEffect } from 'react';

const TextSizeContext = createContext();

export const useTextSize = () => {
  const context = useContext(TextSizeContext);
  if (!context) {
    throw new Error('useTextSize must be used within a TextSizeProvider');
  }
  return context;
};

export const textSizeConfig = {
  'extra-small': {
    label: 'XS',
    messageText: 'text-[10px]',
    inputText: 'text-[10px]',
    headerText: 'text-xs',
    buttonText: 'text-[10px]',
    bodyText: 'text-[10px]',
    labelText: 'text-[10px]',
    avatarSize: 'h-5 w-5',
    minHeight: 'min-h-[50px]',
    padding: 'p-1.5',
    cardPadding: 'p-2.5',
    spacing: 'space-y-1.5'
  },
  small: {
    label: 'S',
    messageText: 'text-xs',
    inputText: 'text-xs',
    headerText: 'text-sm',
    buttonText: 'text-xs',
    bodyText: 'text-xs',
    labelText: 'text-xs',
    avatarSize: 'h-6 w-6',
    minHeight: 'min-h-[60px]',
    padding: 'p-2',
    cardPadding: 'p-3',
    spacing: 'space-y-2'
  },
  medium: {
    label: 'M',
    messageText: 'text-sm',
    inputText: 'text-sm',
    headerText: 'text-base',
    buttonText: 'text-sm',
    bodyText: 'text-sm',
    labelText: 'text-sm',
    avatarSize: 'h-8 w-8',
    minHeight: 'min-h-[80px]',
    padding: 'p-3',
    cardPadding: 'p-4',
    spacing: 'space-y-3'
  },
  large: {
    label: 'L',
    messageText: 'text-base',
    inputText: 'text-base',
    headerText: 'text-lg',
    buttonText: 'text-base',
    bodyText: 'text-base',
    labelText: 'text-base',
    avatarSize: 'h-10 w-10',
    minHeight: 'min-h-[100px]',
    padding: 'p-4',
    cardPadding: 'p-5',
    spacing: 'space-y-4'
  },
  'extra-large': {
    label: 'XL',
    messageText: 'text-lg',
    inputText: 'text-lg',
    headerText: 'text-xl',
    buttonText: 'text-lg',
    bodyText: 'text-lg',
    labelText: 'text-lg',
    avatarSize: 'h-12 w-12',
    minHeight: 'min-h-[120px]',
    padding: 'p-5',
    cardPadding: 'p-6',
    spacing: 'space-y-5'
  }
};

export const TextSizeProvider = ({ children }) => {
  // Load text size from localStorage on initialization
  const getInitialTextSize = () => {
    const savedSize = localStorage.getItem('app-textSize');
    return savedSize && textSizeConfig[savedSize] ? savedSize : 'medium';
  };

  const [textSize, setTextSizeState] = useState(getInitialTextSize);

  const setTextSize = (size) => {
    if (textSizeConfig[size]) {
      setTextSizeState(size);
      localStorage.setItem('app-textSize', size);
    }
  };

  const currentTextSize = textSizeConfig[textSize];

  const value = {
    textSize,
    setTextSize,
    currentTextSize,
    textSizeConfig
  };

  return (
    <TextSizeContext.Provider value={value}>
      {children}
    </TextSizeContext.Provider>
  );
};

